<template>
  <v-modal id="case-history-modal" size="xl" @closed="$emit('closed')">
    <modal-header>
      <modal-title>
        {{ $t("clientTour.caseHistory") }}
      </modal-title>
    </modal-header>
    <modal-body>
      <div v-if="loading" class="d-flex justify-content-center mt-16">
        <v-loader size="sm" />
      </div>
      <div v-else class="table-responsive table-for-cases">
        <div class="case-list w-100">
          <!-- Header Row -->
          <div class="case-header d-flex fw-bold pb-12 mb-12">
            <div class="w-35">Name</div>
            <div class="w-15 d-flex justify-content-center">Type</div>
            <div class="w-30">Route / Dates</div>
            <div class="w-10">version</div>
            <div class="w-10 d-flex justify-content-center">Actions</div>
          </div>

          <template v-if="caseVersionData.length > 0">
            <div
              class="case-row d-flex align-items-center justify-content-between rounded cursor-pointer mb-12"
              v-for="(clientCase, idx) in caseVersionData"
              :key="idx"
            >
              <div class="d-flex align-items-center w-35">
                <div class="image-container rounded-sm bg-tertiary me-12">
                  {{ clientCase?.client?.name.charAt(0) }}
                </div>
                <div>
                  <router-link
                    v-if="!clientCase.deletedAt"
                    :to="{ name: 'case-v2.show', params: { caseId: clientCase.activity_id } }"
                    class="py-12"
                  >
                    {{ clientCase?.name }}
                  </router-link>
                  <a v-else class="py-12" href="#">
                    {{ clientCase?.name }}
                  </a>
                </div>
              </div>
              <div class="w-15 d-flex justify-content-center">
                <div class="case-type-badge image-container rounded-sm p-16">
                  {{ findConceptByKey("tour.travel_type", clientCase?.travel_type)?.name?.charAt(0) }}
                </div>
              </div>
              <div class="w-30 d-flex flex-column">
                <span>
                  <template v-if="!!clientCase?.origin || !!clientCase?.destination">
                    {{ !!clientCase?.origin ? clientCase?.origin.name : "-" }}
                    <v-icon icon="arrow-right" size="xs" class="px-8" />
                    {{ !!clientCase.destination ? clientCase.destination.name : "-" }}
                  </template>
                  <template v-else>-</template>
                </span>
                <span>
                  {{ formatDateTime(clientCase.start_date) }}
                  {{ !!clientCase.end_date ? ` - ${formatDateTime(clientCase.end_date)}` : "" }}
                </span>
              </div>
              <div class="w-10">
                {{ clientCase.version }}
              </div>
              <div class="w-10 d-flex align-items-center justify-content-center">
                <router-link
                  :aria-label="$t('tourClient.tour')"
                  :to="{ name: 'case-v2.show', params: { caseId: clientCase.activity_id } }"
                  class="btn btn-icon btn-view rounded-pill bg-secondary"
                >
                  <v-icon icon="arrow-right" />
                </router-link>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="d-flex justify-content-center">
              <div class="text-center">No case history found</div>
            </div>
          </template>
        </div>
      </div>
    </modal-body>
  </v-modal>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { findConceptByKey } from "@/helpers/ConceptHelper";
import { DateTime } from "luxon";
import api from "@/api";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

const { t } = useI18n();
const toast = useToast();
const emit = defineEmits(["closed"]);
const props = defineProps({
  caseHistoryModalData: {
    type: Object,
    required: true,
    default: null,
  },
  agency: {
    type: String,
    required: true,
  },
});

const caseVersionData = ref([]);
const loading = ref(false);

const formatDateTime = (date: string, format = "dd/MM/yyyy") => {
  if (!date) return "";

  // ✅ Parse as a local date (ignore timezone offset)
  return DateTime.fromISO(date, { zone: "utc" }).toFormat(format);
};

const fetchCaseHistory = async () => {
  loading.value = true;
  try {
    const respone = await api.case.caseVersion(props.agency, props.caseHistoryModalData.id);

    caseVersionData.value = respone.content;
  } catch (error: any) {
    console.error(error);
    toast.error(error?.response?.data?.message || "Something went wrong");
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchCaseHistory();
});
</script>

<style scoped>
.image-container {
  flex: none;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 3rem;
  font-size: 1.5rem;
  color: var(--uq-light);
}

.case-type-badge {
  background-color: #0d6efd;
}

.btn-view {
  padding: 0.375rem;
}

.case-row {
  border: 1px solid #c1c1c1;
  background-color: transparent;
  border-radius: 1rem;
  padding: 12px;
}
</style>
